import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import dataModel from '../../../models/DataModel.js';
import AnalyticsLogger from '../../../helpers/analytics/analyticsLogger';
import naFormIcon from '../../../assets/images/na-form.svg';
import StepApproval from '../../../components/TaskCompletionStepsMobile/StepApproval';
import CommentPopup from '../../../components/TaskCompletionStepsMobile/CommentPopup';
import BaseComponent from '../../../baseClasses/BaseComponent';
import DataSource from '../../../dao/DataSource/DataSource';
import TaskCompletionStepComments from '../../TaskCompletionStepComments';

import './FormUpload.scss';
import Input from '../../Input/index.js';
import Textarea from '../../Textarea/index.js';

const completionCiteriaSelector = {
  completion_criteria: {
    attachments: {
      additionalFields: ['upload', 'photo_longitude', 'photo_latitude', 'photo_taken_at'],
      conversions: {},
      user: {},
    },
    comments: { user: { additionalFields: ['profile_image'] } },
    event_histories: { activity: {}, activity_sub_type: {}, user_who_performed_action: {} },
    form_fields: {},
  },
};

export class FormUpload extends BaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      completionStep: null,
      enableSubmit: false,
      send: false,
      showKeyboard: true,
    };

    this.yesNoChoices = [{ name: 'Yes' }, { name: 'No' }];
    this.setStateInProgress = false;

    const currentRole = props.currentUser ? props.currentUser.role : null;
    this.analyticsLogger = new AnalyticsLogger({
      currentRole,
      isMobile: !!window.cordova,
    });

    this.determineSubmitEnabled = this.determineSubmitEnabled.bind(this);
    this.updateInputValue = this.updateInputValue.bind(this);
    this.handleOnButtonClick = this.handleOnButtonClick.bind(this);
  }

  handleCSUpdate(completionStep, action, acData) {
    this.setState({ completionStep });
    this.determineSubmitEnabled(completionStep);

    if (!this.state.fieldTypesToState || acData) {
      this.handleStoreInputValues(completionStep);
    }

    if (action == 'completeCompletionCriterion') {
      if (completionStep) {
        if (this.props.handleChildCompletionStepUpdate) {
          this.props.handleChildCompletionStepUpdate(completionStep);
        }
      }
    }
    else if (this.props.isMobile && (action == 'approveCompletionCriterion' || action == 'reviewCompletionCriterion')) {
      this.props.history.goBack();
    }
  }

  componentDidMount() {
    const { completionStep } = this.props;

    this.csDataSource = new DataSource('CompletionCriterion', completionStep, completionCiteriaSelector, this.handleCSUpdate);
    this.csDataSource.get();
  }

  componentWillUnmount() {
    if (this.csDataSource) {
      this.csDataSource.release();
      this.csDataSource = null;
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.completionStep != this.props.completionStep && !this.setStateInProgress) {
      this.csDataSource.get();
    }
  }

  handleStoreInputValues(completionStep) {
    //store text values in state for changing inputs : store here instead of looping over state every time we change a CS
    if (completionStep.form_fields) {
      let fieldTypesToState = {};
      completionStep.form_fields.map(field => {
        if (field && (field.field_type == 'numeric' || field.field_type == 'text')) {
          const response = field.response[0] ? field.response[0] : '';
          const fieldStateResponse = `field-response-${field.id}`;
          fieldTypesToState[fieldStateResponse] = response;
        }
        this.setState({ ...fieldTypesToState });

        return field;
      });
    }
  }

  handleSubmit() {
    this.setState({ enableSubmit: false });
    this.analyticsLogger.logFormSubmitEvent();
    this.csDataSource.performAction('completeCompletionCriterion', this.state.completionStep, true);
  }

  createFormRow(field, completionStep) {
    const { userStepPermissions } = this.props;
    let fieldType = [];
    let isPromptAnswered;

    const formReadOnly = this.props.projectTemplate || !!completionStep.completed_at || !userStepPermissions.has('step_complete');
    
    switch (field.field_type) {
    case 'multiple_choice':
      fieldType = this.createRadioChoices(field, field.config.choices, formReadOnly);
      isPromptAnswered = field.response[0] ? true : false;
      break;
    case 'yes_no':
      fieldType = this.createRadioChoices(field, this.yesNoChoices, formReadOnly);
      isPromptAnswered = field.response[0] ? true : false;
      break;
    case 'numeric':
      if (this.state[`field-response-${field.id}`] != undefined) {
        isPromptAnswered = this.state[`field-response-${field.id}`] ? true : false;
        fieldType.push(
            <div className='form-choice' key={'numeric' + field.id}>
              <Input
                id={field.id}
                disabled={formReadOnly}
                className={'form-choice__input form-choice-' + field.order + '-' + completionStep.id + (formReadOnly ? ' read-only' : '')}
                placeholder='Enter a Number'
                type={!formReadOnly ? 'number' : 'text'}
                name='numeric answer'
                value={this.state[`field-response-${field.id}`]}
                onChange={this.updateInputValue}
                onKeyDown={this.handleKeyDownEnter}
                onBlur={this.handleFormFieldUpdate}
              />
            </div>
          );
      }
      break;
    default:
      if (this.state[`field-response-${field.id}`] != undefined) {
        isPromptAnswered = this.state[`field-response-${field.id}`] ? true : false;
        fieldType.push(
            <div className='form-choice' key={'text' + field.id}>
              <Input
                id={field.id}
                disabled={formReadOnly}
                className={'form-choice__input form-choice-' + field.order + '-' + completionStep.id + (formReadOnly ? ' read-only' : '')}
                type='text'
                name='text answer'
                value={this.state[`field-response-${field.id}`]}
                placeholder='Enter Text'
                onChange={this.updateInputValue}
                onKeyDown={this.handleKeyDownEnter}
                onBlur={this.handleFormFieldUpdate}
              />
            </div>
          );
      }
    }

    const isOdd = field.order % 2 != 0 ? ' form-row--odd' : '';

    //logic here to decide whether or not the order is blue
    //for text inputs, check the state
    //for multiple choice and yesno, check the response
    if (field.field_type == 'multiple_choice' || field.field_type == 'yes_no') {
      return (
        <div className={'form-row' + isOdd} key={'field-row' + field.id}>
          <div className="focus-box"  tabIndex={!formReadOnly ? '0' : '-1'} >
          <div className={'form-order' + (isPromptAnswered ? ' form-order--completed' : '')}>
            {field.order}
          </div>
          <div className='form-info'>
            <div className='form-info__prompt'>
              {field.prompt}
            </div>
            <div className='form-info__choices'>
              {fieldType}
            </div>
          </div>
          </div>
        </div>
      );
    }
    else {
      return (
          <div className={'form-row' + isOdd} key={'field-row' + field.id} >
          <div className="focus-box"  >
            <div className={'form-order' + (isPromptAnswered ? ' form-order--completed' : '')}>
              {field.order}
            </div>
            <div className='form-info'>
              <div className='form-info__prompt'>
                {field.prompt}
              </div>
              <div className='form-info__choices'>
                {fieldType}
              </div>
            </div>
            </div>
          </div>
        );
    }
  }

  updateInputValue(e) {
    const fieldStateResponse = `field-response-${e.currentTarget.id}`;
    this.setState({ [fieldStateResponse]: e.target.value });
  }

  handleKeyDownEnter(e) {
    if (e.keyCode == 13) {
      const { completionStep } = this.state;
      let field = completionStep.form_fields.find(field => { return +e.currentTarget.id == field.id; });
      this.handleFormUpdate(field);
    }
    //on enter focus on next choice
  }

  handleButtonKeyDownEnter(e) {
    const { completionStep } = this.state;
    const id = parseInt(e.currentTarget.dataset.fieldId, 10);
    const choice = e.currentTarget.dataset.choiceName;
    if (id && choice) {
      let field = completionStep.form_fields.find(field => { return id == field.id; });
      if (e.keyCode == 13 || e.keyCode == 32) {

        this.handleFormUpdate(field, choice);
      }
      else if ((e.keyCode == 38 || e.keyCode == 40) && (field.field_type == 'yes_no' || field.field_type == 'multiple_choice')) {
        const { completionStep } = this.state;

        e.preventDefault();

        // what is the next 
      
        let choices = field.field_type == 'yes_no' ? this.yesNoChoices : field.config.choices;
        let nextChoice = -1;
        if (e.keyCode == 38) {
          nextChoice = choices.findIndex(c => { return c.name == choice; }) - 1;
        }
        else if (e.keyCode == 40) {
          nextChoice = choices.findIndex(c => { return c.name == choice; }) + 1;
        }
        if (nextChoice >= 0 && nextChoice < choices.length) {
          let nextElement = document.getElementById('form-choice-' + field.order + '-' + completionStep.id + choices[nextChoice].name);
          if (nextElement) {
            nextElement.focus();
          }
        }
      }
      //on enter focus on next choice
    }
  }

  confirmChange = () => {
    //DEPRICATED - leave for future possibility
    dataModel.systemPopup = null;
    this.csDataSource.performAction('completeCompletionCriterion', this.state.completionStep, true);
  }

  cancelChange = () => {
    dataModel.systemPopup = null;
    this.setState({ updatedFormField: null });
  };

  handleIncompleteForm = () => {
    //DEPRICATED - leave for future possibility
    const stepName = this.state.completionStep.name
      .split(' ')
      .map(
        word => word.substr(0, 1).toUpperCase() + word.substr(1).toLowerCase()
      )
      .join(' ');

    const message = (
      <div>
        <p>Do you want to change the form values for {stepName}?</p>
      </div>
    );

    dataModel.showGlobalModal('SYSTEM_POPUP', {
      height: 184,
      width: 320,
      message: message,
      cancelButtonText: 'Cancel',
      okButtonText: 'Yes',
      handleOwnerOk: () => {
        this.confirmChange();
      },
      handleOwnerCancel: () => {
        this.cancelChange();
      },
    });
  };

  async handleFormUpdate(field, response = null, shouldDeselect = false) {
    const { completionStep } = this.state;
      //response is passed in for yes/no and mc, response is store in state for inputs
    if (!response) {
        //if the state stores an empty string, set it to null
      response = this.state[`field-response-${field.id}`] ? this.state[`field-response-${field.id}`] : null;
    }

    if (shouldDeselect) {
      response = null;
    }

    field.response = [response];

    if (completionStep.completed_at) {
      this.setState({ updatedFormField: field });
      this.handleIncompleteForm();
    }
    else {
      await dataModel.formFieldsDAO.updateFormField(field, completionStep.id, { recordDescriptor: { form_fields: {} } }).catch(error => {
      });
    }
  }

  handleFormFieldUpdate(e) {
    const { completionStep } = this.state;

    let field = completionStep.form_fields.find(field => { return +e.currentTarget.id == field.id; });
    if (field) {
      this.handleFormUpdate(field);
    }
  }

  handleChoiceSelection(e) {
    //check to see if it was already selected, if so, pass indicator to deselect
    const { completionStep } = this.state;
    const id = parseInt(e.currentTarget.dataset.fieldId, 10);
    const choice = e.currentTarget.dataset.choiceName;

    if (id && choice) {
      let field = completionStep.form_fields.find(f => { return id == f.id; });

      let shouldDeselect = false;
      if (field.response.includes(choice)) {
        shouldDeselect = true;
      }
    
      this.handleFormUpdate(field, choice, shouldDeselect);
    }
  }

  createRadioChoices(field, choices, formReadOnly) {
    const { completionStep } = this.state;
    //response will have selected choice
    return choices.map((choice, index) => {
      let selectedDisplay = <div className='form-choice__radio'></div>;

      if (field.response.includes(choice.name)) {
        if (!formReadOnly) {
          selectedDisplay = <div className='form-choice__radio'>
            <div className={'form-choice__radio-selected'}></div>
          </div>;
        }
        else {
          selectedDisplay = <div className='form-choice__radio-filled'></div>;
        }
      }

      return (
        <div
          id={'form-choice-' + field.order + '-' + completionStep.id + '-' + field.id}
          data-field-id={field.id}
          data-choice-name={choice.name}
          className={'form-choice form-choice-' + field.order + '-' + completionStep.id} 
          onClick={!formReadOnly ? this.handleChoiceSelection : null}
          onKeyDown={!formReadOnly ? this.handleButtonKeyDownEnter : null}
          key={choice.name + index}
          tabIndex={!formReadOnly ? '0' : '-1'}
        >

            { selectedDisplay }
          <label className='form-choice__name'> { choice.name } </label>
        </div>
      );
    });

  }

  handleNotApplicableButton() {
    const { completionStep } = this.state;

    //check if a response exists
    const responseFound = completionStep.form_fields.find(field => field.response[0]);

    if (responseFound) {
      //display popup
      dataModel.showGlobalModal('SYSTEM_POPUP', {
        height: 184,
        width: 320,
        message: this.textMap.markPartialFormAsNA,
        cancelButtonText: 'Cancel',
        okButtonText: 'Yes',
        handleOwnerOk: () => {
          this.handleNotApplicableSend();
        },
        handleOwnerCancel: () => {},
      });
    }
    else {
      this.handleNotApplicableSend();
    }

  }
  
  async handleNotApplicableSend() {
    const { completionStep } = this.state;
    this.csDataSource.performAction('markNotApplicable', completionStep);
  }

  async handleUndoNotApplicable() {
    const { completionStep } = this.state;
    this.csDataSource.performAction('unmarkNotApplicable', completionStep);
  }

  createButtonList() {
    const { userStepPermissions, handleCommentsButton } = this.props;
    const { completionStep } = this.state;
    
    let buttons = [];

    if (!completionStep.not_applicable && !completionStep.completed_at && userStepPermissions.has('step_complete')) {
      buttons.push(
        <div
          className="button na-button"
          key="no-image-button"
          onClick={this.handleNotApplicableButton}
          data-qa="not-applicable-button"
        />
      );
    }
    else if (
      completionStep.not_applicable && (userStepPermissions.has('unmarkNA') ||
      ((userStepPermissions.has('step_complete') || userStepPermissions.has('step_update')) &&  
      !completionStep.reviewed_at && !completionStep.approved_at &&
      (completionStep.status != 'completed'))
    )) {
      buttons.push(
        <div
          className="button undo-na-button"
          key="undo-na-button"
          onClick={this.handleUndoNotApplicable}
          data-qa="not-applicable-button"
        />
      );
    }

    if (this.props.isMobile) {
      buttons.push(
        <div
          key="comment-button"
          className="button comments-button"
          onClick={handleCommentsButton}
        >
          {completionStep.comments.length > 0 && <div className="indicator-dot" />}
        </div>
      );
    }

    return buttons;
  }

  determineSubmitEnabled(completionStep) {
    //is there a better way to do this instead of looping responses
    if (completionStep) {
      if (completionStep.completed_at) {
        this.setState({ enableSubmit: false });
      }
      else {
        const notCompletedSteps = completionStep && completionStep.form_fields && completionStep.form_fields.find(step => !step.response || !step.response.length ||
          !step.response[0]);
        if (!notCompletedSteps) {
          this.setState({ enableSubmit: true });
        }
        else if (this.state.enableSubmit) {
          this.setState({ enableSubmit: false });
        }
      }
    }
  }

  handleStepApprove() {
    const { userStepPermissions } = this.props;
    const { completionStep } = this.state;
    if (userStepPermissions.has('is_approver')) {
      this.handleApproved(completionStep);
    }
    else if (userStepPermissions.has('is_reviewer')) {
      this.handleReviewed(completionStep);
    }
  }

  handleStepReject() {
    const { userStepPermissions } = this.props;
    if (userStepPermissions.has('accept/reject')) {
      this.handleRejection();
    }
  }

  async handleApproved(completionStep) {
    this.csDataSource.performAction('approveCompletionCriterion', completionStep);
  }

  async handleReviewed(completionStep) {
    this.csDataSource.performAction('reviewCompletionCriterion', completionStep);
  }

  handleRejection() {
    this.setState({ rejectionComment: true });
  }

  handleCancelComment() {
    this.setState({
      rejectionComment: false,
    });
  }

  async handleCommentSent(type, commentText) {
    this.setState({ rejectionComment: false });
    
    const { completionStep } = this.state;
    this.csDataSource.performAction('rejectCompletionCriterion', completionStep, commentText);
    
    this.setState({ commentText: '' });

    if (this.commentInput) {
      this.commentInput.blur();
    }
    if (dataModel.mobileBridge.isMobile) {
      this.props.history.goBack();
    }
  }

  handleRejectionKeyDown(e, type) {
    if (e.keyCode == 27) {
      this.setState({ rejectionComment: false });
    }else if (e.keyCode == 13 || e.keyCode == 40) {
      // e.preventDefault();
      // this.handleCommentSent(type, this.state.commentText)
      this.setState({ rejectionComment: true });
      this.state.showKeyboard=false;
      this.handleSetInputElement(e.target,e);
    }
    // else if (e.keyCode == 13 || e.keyCode == 40) {
    //   e.preventDefault();
    //   this.handleCommentSent(type, this.state.commentText);
    // }
  }
  
  handleOnButtonClick(){
    this.state.send=true;
    if(this.state.commentText){
      this.handleCommentSent('reject', this.state.commentText);
    }
  }

  handleSetInputElement(element,e) {
    if (element && e==null && this.state.showKeyboard) {
      this.inputElement = element;
      this.inputElement.focus(); 
    }  
    else if(e && element && !this.state.showKeyboard){
      this.inputElement = element;
      this.inputElement.blur();   
    }
  }

  handleCommentChange(e) {
    if(e.target.value.trim()!=''){
      this.setState({ commentText: e.target.value });
    }
    else{
      this.setState({ commentText: '' });
    }
  }
  
  render() {
    const { userStepPermissions, task } = this.props;
    const { completionStep, enableSubmit } = this.state;
    
    if (!completionStep) {
      return null;
    }
    
    //form is read only if the completion step is completed at OR if the user is not allowed to complete the step
    const formReadOnly = this.props.projectTemplate || !!completionStep.completed_at || !userStepPermissions.has('step_complete');
    let buttons = this.createButtonList();
    let formDisplay;

    if (completionStep.not_applicable) {
      formDisplay = (
        <div className='na-form-container'>
          <img
            className="mobile-misc-default-image-display"
            src={naFormIcon}
            alt="not applicable"
            data-qa="not-applicable-image"
          />
        </div>
      );
    }
    else {
      formDisplay = completionStep.form_fields ? completionStep.form_fields.map((field) => {
        return this.createFormRow(field, completionStep);
      }): [];

    }
    
    let submitButtons;
    if (!completionStep.completed_at && !formReadOnly) {
      //add an extra row for padding at bottom of form
      submitButtons = <div key='submit-row' className='form-submit-row' tabIndex="0" >
          <div className={'form-submit-button' + (enableSubmit ? ' form-submit-button--enabled' : '')} onClick={enableSubmit ? this.handleSubmit : null}>
            { this.textMap.submit.toUpperCase() }
          </div>
      </div>;
    }

    //add an extra row for padding at bottom of 
    if (formDisplay && formDisplay.push) {
      formDisplay.push(<div key='empty' className='form-row__empty'></div>);
    }

    let comments = null;
    if (!this.props.isMobile) {
      comments = <div className='form-comments'>
        <TaskCompletionStepComments
          id={'task-completion-step-comments-' + completionStep.id}
          task={task}
          completionStep={completionStep} 
        />
      </div>;
    }

    let reviewApproveButtons;
    let commentDisplay = null;
    let sendButton = (
      <div className="send-button disabled">
        <div className="send-text disabled">SEND</div>
      </div>
    );
    if (this.state.commentText && this.state.commentText!='') {
      sendButton = (
        <div className="send-button" onClick={this.handleOnButtonClick}>
          <div className="send-text">SEND</div>
        </div>
      );
    }
    if (this.state.rejectionComment) {
      if (dataModel.mobileBridge.isMobile) {
        commentDisplay = (
          <div className="comment-input">
            <Textarea
              rows={3}
              ref={element => { this.handleSetInputElement(element,null); }}
              className="text-input"
              placeholder="You must leave a comment to reject a Completion Step."
              onChange={this.handleCommentChange}
              value={this.state.commentText}
              onKeyDown={e => this.handleRejectionKeyDown(e, 'reject')}
              dataId={completionStep.id}
              style={{ resize: 'none' }}
            />
            {sendButton}
          </div>
        );
      } else {
        const element = document.getElementById(
          'task-completion-step-comments-' + completionStep.id
        );
        const rect = element.getBoundingClientRect();
        commentDisplay = (
          <CommentPopup
            task={task}
            completionStep={completionStep}
            height={element.clientHeight}
            width={element.clientWidth}
            left={rect.left}
            top={rect.top - 20}
            type={'REJECTED'}
            commentSent={commentText =>
              this.handleCommentSent('reject', commentText)
            }
            placeholder={
              'You must leave a comment to reject a Completion Step.'
            }
            cancelComment={this.handleCancelComment}
          />
        );
      }
    }
    
    if (!this.state.rejectionComment &&
      userStepPermissions &&
      userStepPermissions.has('accept/reject') && !completionStep.rejected_at) {
    
      reviewApproveButtons = (
        <div className={'photo-approve-reject-buttons' + (completionStep.not_applicable ? ' na' : '')}>
          <StepApproval key={(task.has_reviewer && (userStepPermissions.has('is_reviewer') || this.props.currentUser.super_admin) &&
            !completionStep.reviewed_at ? '_review' : '_approve') + completionStep.id} userStepPermissions={userStepPermissions}
            handleStepReject={this.handleStepReject}
            handleStepApprove={this.handleStepApprove}
          />
        </div>
      );
    }

    const displayInstructionsOnWeb = completionStep.instructions && !this.props.isMobile;

    if (this.props.projectTemplate) {
      submitButtons = null;
      reviewApproveButtons = null;
      comments = null;
      commentDisplay = null;
      buttons = null;
    }

    return (
      <div className={'FormUpload' + (formReadOnly ? ' FormUpload--read' : '')}>
        { displayInstructionsOnWeb &&
          <div className="misc-instructions">
            <div className="instruction-label">Instructions</div>
            <div className="instruction-text">{completionStep.instructions}</div>
          </div>
        }
        <div className="form-inner">
          <div className={'form-display' + (completionStep.not_applicable ? ' form-display--na' : '')}>
            { formDisplay }
          </div>
          <div
            className={`button-list ${
              userStepPermissions.has('accept/reject')
                ? 'review-approve'
                : ''}
              `}
          >
            { buttons }
          </div>
        </div>
        { submitButtons }
        { reviewApproveButtons }
        { comments }
        { commentDisplay }
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    dataModel: dataModel,
    mobileBridge: dataModel.mobileBridge,
    permissions: state.new_users.permissions,
    currentUser: state.new_users.currentUser,
  };
};

export default connect(mapStateToProps, null)(withRouter(FormUpload));



// WEBPACK FOOTER //
// ./src/components/Forms/FormUpload/index.js